"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useQuery } from "@tanstack/react-query";
import { type UserProfile } from "@theta/api-client";
import { isLetterAvatarUrl } from "@/lib/avatar";
import { thetaApi } from "@/lib/api/theta";
import { useCurrentUser } from "@/lib/hooks/useCurrentUser";
import { Button, Card, CardContent } from "@/components/ui";
import { retryUnlessRefused } from "@/lib/hooks/queryRetry";

/**
 * "FINISH YOUR PROFILE" — ONE DISMISSIBLE CARD, ON ONE SURFACE (OSO-430)
 *
 * The ask was to NUDGE people into a real name, a picture and (softly) a line
 * about themselves. Deliberately NOT what this is:
 *
 *  - not a blocking onboarding wizard and not a modal — it is a card at the
 *    top of the feed, the one surface everybody lands on, and scrolling past
 *    it costs nothing;
 *  - not a second nudge anywhere else. Nudging in three places is how a
 *    prompt becomes noise;
 *  - not a completeness meter, a settings toggle, a badge or a mail. None of
 *    that was asked for.
 *
 * IT ENDS BY ITSELF. Two ways out, and both are permanent: fill the profile
 * in (`gaps` empties and the card stops rendering for good), or dismiss it
 * (persisted, so it does not come back on the next page load). There is no
 * third state where it reappears to ask again.
 *
 * NO ENDPOINT WAS ADDED. `name` and `avatar` ride on `GET /me`; `bioText`
 * comes from `GET /users/{id}` on the SAME query key the profile page and the
 * MitID banner already use (`["v1", "user", id]`), so the card is a third
 * reader of a cache entry rather than a third request.
 */

/** One thing the profile is still missing. Order is the order we ask in. */
export type ProfileGap = "name" | "avatar" | "bio";

/**
 * What is still missing, in the order the card lists it.
 *
 * THE HALVES ARE ON THE WIRE NOW (OSO-430): `firstName`/`lastName` are the
 * stored fact `name` is generated from, so "has a full name" is simply "both
 * halves present" — no word counting, no `Bruger {id}` sentinel to recognise
 * (the halves are raw; the server never substitutes a label here).
 */
export function profileGaps(profile: {
  id: number;
  firstName: string | null;
  lastName: string | null;
  avatarUrl: string | null;
  bioText: string | null;
}): ProfileGap[] {
  const gaps: ProfileGap[] = [];
  if (
    (profile.firstName?.trim() ?? "") === "" ||
    (profile.lastName?.trim() ?? "") === ""
  ) {
    gaps.push("name");
  }
  if (isLetterAvatarUrl(profile.avatarUrl)) gaps.push("avatar");
  if ((profile.bioText?.trim() ?? "") === "") gaps.push("bio");
  return gaps;
}

/**
 * THE BIO ALONE IS NOT WORTH INTERRUPTING FOR. The report itself hedged it
 * ("måske også en beskrivelse"), so a profile with a full name and a picture
 * is DONE as far as this card is concerned — the bio only ever rides along as
 * an extra line on a card that a missing name or picture had already earned.
 */
export function shouldNudge(gaps: ProfileGap[]): boolean {
  return gaps.includes("name") || gaps.includes("avatar");
}

const DISMISSED_KEY = "profile_nudge_dismissed";

/**
 * `localStorage` THROWS, AND IT THROWS ON THE READ TOO.
 *
 * A browser set to block site data (Chrome's "block all cookies", Firefox's
 * strict mode, an iframe with no storage access) raises `SecurityError` from
 * the `localStorage` ACCESSOR itself, before `getItem` is reached — and the
 * read below happens in an effect, i.e. during commit, so an uncaught throw
 * does not just lose the dismissal: it propagates to the route's error
 * boundary and takes the whole feed down with it. A failed WRITE (quota,
 * Safari private mode) must not stop the card disappearing either.
 *
 * Both are wrapped for that reason, in the shape every other reader of
 * `localStorage` in this app already uses — `lib/hooks/useDraft.ts`,
 * `lib/hooks/useSubmitShortcut.ts`, `InstallPrompt`, `PushPrompt`.
 */
function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === "true";
  } catch {
    // No storage — the card is shown, and dismissing it lasts this page load.
    return false;
  }
}

function rememberDismissed(): void {
  try {
    localStorage.setItem(DISMISSED_KEY, "true");
  } catch {
    // Ignore write failures (quota, privacy mode); the card still goes away.
  }
}

const GAP_COPY: Record<ProfileGap, { label: string; hint: string }> = {
  name: {
    label: "Dit for- og efternavn",
    hint: "Så ved andre, hvem de skriver med.",
  },
  avatar: {
    label: "Et profilbillede",
    hint: "Et billede gør dig nemmere at genkende.",
  },
  bio: {
    label: "En kort beskrivelse (valgfri)",
    hint: "Et par linjer om dig selv.",
  },
};

function GapIcon({ gap }: { gap: ProfileGap }) {
  const common = {
    width: 16,
    height: 16,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  if (gap === "name") {
    return (
      <svg {...common} aria-hidden="true">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    );
  }
  if (gap === "avatar") {
    return (
      <svg {...common} aria-hidden="true">
        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
        <circle cx="12" cy="13" r="4" />
      </svg>
    );
  }
  return (
    <svg {...common} aria-hidden="true">
      <line x1="4" y1="7" x2="20" y2="7" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="17" x2="14" y2="17" />
    </svg>
  );
}

export function ProfileCompletionNudge() {
  const { data: currentUser } = useCurrentUser();
  // Starts hidden: `localStorage` does not exist during SSR, and reading it in
  // render would be a hydration mismatch. The effect below corrects it on
  // mount, so the card can only ever appear — never flash and disappear.
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDismissed(readDismissed());
  }, []);

  /**
   * The same key `/profil/{id}` and `MitidVerificationBanner` read, so the
   * three share one cache entry and one request. `bioText` is the only field
   * here that `GET /me` does not carry.
   */
  const { data: profile } = useQuery<UserProfile>({
    queryKey: ["v1", "user", currentUser?.id],
    queryFn: () => thetaApi.user(currentUser!.id),
    enabled: !!currentUser && !dismissed,
    staleTime: 5 * 60 * 1000,
    retry: retryUnlessRefused,
  });

  if (!currentUser || dismissed || !profile) return null;

  const gaps = profileGaps({
    id: profile.id,
    firstName: profile.firstName,
    lastName: profile.lastName,
    avatarUrl: profile.avatar?.large ?? null,
    bioText: profile.bioText,
  });
  if (!shouldNudge(gaps)) return null;

  function dismiss() {
    rememberDismissed();
    setDismissed(true);
  }

  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <h2 className="font-semibold text-foreground">
              Gør din profil færdig
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              Andre i fællesskabet har lettere ved at genkende dig.
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={dismiss}
            aria-label="Skjul"
            className="shrink-0 px-2 text-muted-foreground"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </Button>
        </div>

        <ul className="mt-4 space-y-2">
          {gaps.map((gap) => (
            <li key={gap} className="flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-muted/50 text-primary shrink-0">
                <GapIcon gap={gap} />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-medium text-foreground">
                  {GAP_COPY[gap].label}
                </span>
                <span className="block text-xs text-muted-foreground">
                  {GAP_COPY[gap].hint}
                </span>
              </span>
            </li>
          ))}
        </ul>

        {/* Straight into the existing edit form — the nudge points at the
            profile page's own editor rather than carrying a second copy of
            it. `?rediger=1` is what opens it, and `asChild` keeps it a real
            `<Link>` rather than a Button with a `push()`, so it middle-clicks
            like every other navigation on the page. */}
        <Button asChild className="mt-4">
          <Link href={`/profil/${currentUser.id}?rediger=1`}>
            Udfyld din profil
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}
